import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle2, Circle, ExternalLink, Download, Sparkles, Package, BookOpen, Send, Building2 } from "lucide-react";

function PublisherSection({ bookId, book }) {
  const [publishers, setPublishers] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [message, setMessage] = useState("");
  const [looking, setLooking] = useState(!!book?.looking_for_publisher);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [pubs, props] = await Promise.all([
          api.get("/publishers"),
          api.get(`/books/${bookId}/proposals`),
        ]);
        setPublishers(pubs.data || []);
        setProposals(props.data || []);
      } catch (err) {
        console.debug("publisher section load failed", err?.message);
      }
    })();
  }, [bookId]);

  const send = async () => {
    if (!selectedId) { toast.error("Pick a publisher"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/books/${bookId}/proposals`, {
        publisher_user_id: selectedId,
        message,
      });
      setProposals((p) => [data, ...p]);
      setSelectedId("");
      setMessage("");
      toast.success("Proposal sent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const toggleLooking = async () => {
    const next = !looking;
    setLooking(next);
    try {
      await api.patch(`/books/${bookId}`, { looking_for_publisher: next });
      toast.success(next ? "Your book is now visible to publishers" : "Hidden from publishers");
    } catch (err) {
      setLooking(!next);
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const selectedPub = publishers.find((p) => p.user_id === selectedId);
  const acceptingPublishers = publishers.filter((p) => p.listing_status === "active");

  return (
    <section
      className="p-6 rounded-lg"
      style={{ background: "white", border: "1px solid var(--bf-border)" }}
      data-testid="publish-publisher-section"
    >
      <div className="flex items-start gap-3 mb-4">
        <Building2 className="w-5 h-5 mt-1" style={{ color: "var(--bf-primary)" }} />
        <div className="flex-1">
          <h3 className="font-serif text-2xl">Send to a publisher</h3>
          <p className="text-sm mt-1" style={{ color: "var(--bf-text-muted)" }}>
            Pick from admin-approved publishers below. They&apos;ll receive your book and decide whether to accept.
          </p>
        </div>
      </div>

      {/* Looking-for-publisher flag */}
      <label className="flex items-center gap-2 mb-5 text-sm cursor-pointer" data-testid="publish-looking-toggle">
        <input
          type="checkbox"
          checked={looking}
          onChange={toggleLooking}
          className="rounded"
        />
        <span>Make this book visible in publishers&apos; &ldquo;Browse books&rdquo; tab (publishers can see it without a direct proposal)</span>
      </label>

      {/* Publisher picker + send */}
      {acceptingPublishers.length === 0 ? (
        <div className="p-4 rounded text-sm" style={{ background: "var(--bf-surface)", color: "var(--bf-text-muted)" }} data-testid="publish-no-publishers">
          No publishers are currently accepting books. Check the public <a href="/publishers" className="underline">directory</a> for updates.
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <Label className="mb-1.5 block">Choose a publisher</Label>
            <select
              className="border rounded px-3 py-2 text-sm w-full max-w-md"
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              data-testid="publish-publisher-select"
            >
              <option value="">— Pick a publisher —</option>
              {acceptingPublishers.map((p) => (
                <option key={p.user_id} value={p.user_id}>
                  {p.company_name}
                  {p.base_fee != null ? ` · ${p.currency} ${Number(p.base_fee).toLocaleString()}` : ""}
                  {p.turnaround ? ` · ${p.turnaround}` : ""}
                </option>
              ))}
            </select>
          </div>

          {selectedPub && (
            <div className="text-xs p-3 rounded" style={{ background: "var(--bf-surface)" }} data-testid="publish-publisher-preview">
              <p className="mb-1"><strong>{selectedPub.company_name}</strong> — {selectedPub.tagline}</p>
              {selectedPub.discount_note && (
                <p style={{ color: "var(--bf-primary)" }}>{selectedPub.discount_note}</p>
              )}
              <p className="mt-1" style={{ color: "var(--bf-text-muted)" }}>
                Services: {(selectedPub.services || []).join(", ") || "—"}
              </p>
              {selectedPub.terms_and_conditions && (
                <details className="mt-2">
                  <summary className="cursor-pointer">View terms &amp; conditions</summary>
                  <pre className="whitespace-pre-wrap mt-1 text-[11px] font-sans">{selectedPub.terms_and_conditions}</pre>
                </details>
              )}
            </div>
          )}

          <div>
            <Label className="mb-1.5 block">Cover note (optional)</Label>
            <Textarea
              rows={3}
              placeholder="Tell the publisher about your book, royalty expectations, timeline…"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid="publish-publisher-message"
            />
          </div>

          <Button
            onClick={send}
            disabled={!selectedId || sending}
            className="text-white"
            style={{ background: "var(--bf-primary)" }}
            data-testid="publish-publisher-send"
          >
            <Send className="w-4 h-4 mr-2" /> {sending ? "Sending…" : "Send proposal"}
          </Button>
        </div>
      )}

      {/* Existing proposals */}
      {proposals.length > 0 && (
        <div className="mt-6 pt-5 border-t" style={{ borderColor: "var(--bf-border)" }}>
          <p className="text-sm font-medium mb-3">Your proposals</p>
          <ul className="space-y-2" data-testid="publish-proposals-list">
            {proposals.map((p) => (
              <li
                key={p.proposal_id}
                className="flex items-center justify-between gap-3 p-3 rounded text-sm"
                style={{ background: "var(--bf-surface)" }}
                data-testid={`publish-proposal-${p.proposal_id}`}
              >
                <span><strong>{p.publisher_name}</strong> — sent {new Date(p.created_at).toLocaleDateString()}</span>
                <span className="text-xs px-2 py-0.5 rounded-full" style={{
                  background: p.status === "accepted" ? "#DCFCE7" : p.status === "rejected" ? "#FEE2E2" : "#FEF3C7",
                  color: p.status === "accepted" ? "#166534" : p.status === "rejected" ? "#991B1B" : "#92400E",
                }}>{p.status}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

export default function PublishPanel({ bookId, book }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bundling, setBundling] = useState(false);
  const [kdpBusy, setKdpBusy] = useState(false);
  const [kdpResult, setKdpResult] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/books/${bookId}/publish/checklist`);
      setData(data);
    } catch {
      toast.error("Failed to load checklist");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [bookId]);

  const downloadBundle = async () => {
    setBundling(true);
    try {
      const token = localStorage.getItem("bf_token");
      const resp = await fetch(`${API}/books/${bookId}/publish/bundle`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!resp.ok) throw new Error("Failed");
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${book?.slug || "book"}-publish-bundle.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Bundle ready — check your downloads");
    } catch {
      toast.error("Bundle download failed");
    } finally {
      setBundling(false);
    }
  };

  const downloadTypeset = async (trim) => {
    try {
      const token = localStorage.getItem("bf_token");
      const resp = await fetch(`${API}/books/${bookId}/publish/typeset-pdf?trim=${encodeURIComponent(trim)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(t || "Failed");
      }
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${book?.slug || "book"}-${trim}-typeset.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(`Typeset PDF (${trim}) ready`);
    } catch (err) {
      toast.error(err?.message || "Typeset PDF failed");
    }
  };

  const copyMetadata = async () => {
    const m = {
      title: book?.title,
      subtitle: book?.theme,
      description: book?.short_summary || book?.description,
      categories: book?.genres,
      isbn: book?.isbn,
    };
    await navigator.clipboard.writeText(JSON.stringify(m, null, 2));
    toast.success("Metadata copied — paste into KDP/IngramSpark");
  };

  const runKdpKeywords = async () => {
    setKdpBusy(true);
    setKdpResult("");
    try {
      const { data } = await api.post(`/books/${bookId}/publish/kdp-keywords`, {});
      setKdpResult(data.result);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI failed");
    } finally {
      setKdpBusy(false);
    }
  };

  if (loading || !data) {
    return <p className="text-sm" style={{ color: "var(--bf-text-muted)" }} data-testid="publish-loading">Loading…</p>;
  }

  const { items, total_words, chapter_count, ready, providers, review_done } = data;
  const enabledProviders = providers.filter((p) => p.enabled);

  return (
    <div className="max-w-4xl space-y-10" data-testid="publish-panel">
      <div>
        <h2 className="font-serif text-3xl mb-1">Publish</h2>
        <p className="text-sm" style={{ color: "var(--bf-text-muted)" }}>
          {chapter_count} chapter{chapter_count === 1 ? "" : "s"} · {total_words.toLocaleString()} words
        </p>
      </div>

      {/* PUBLISHER SECTION — added ABOVE the existing workflow */}
      <PublisherSection bookId={bookId} book={book} />

      {/* REVIEW GATE BANNER */}
      {!review_done && (
        <div
          className="p-5 rounded-lg flex items-start gap-3"
          style={{ background: "#FEF3C7", border: "1px solid #F59E0B" }}
          data-testid="publish-review-banner"
        >
          <Sparkles className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: "#92400E" }} />
          <div className="flex-1">
            <p className="font-medium text-sm" style={{ color: "#92400E" }}>
              Run an AI review before publishing
            </p>
            <p className="text-xs mt-1" style={{ color: "#92400E" }}>
              External provider deep links (KDP, IngramSpark, Lulu) and AI KDP keywords are unlocked once the review has been run. Self-publish bundle download is still allowed below.
            </p>
          </div>
        </div>
      )}

      {/* CHECKLIST */}
      <section data-testid="publish-checklist">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-2xl">Readiness checklist</h3>
          <Badge variant={ready ? "default" : "outline"} style={ready ? { background: "var(--bf-primary)", color: "white" } : {}} data-testid="publish-ready-badge">
            {ready ? "Ready to publish" : "Not ready"}
          </Badge>
        </div>
        <ul className="space-y-2.5">
          {items.map((it) => (
            <li
              key={it.id}
              className="flex items-start gap-3 p-3 rounded-md"
              style={{ background: "white", border: "1px solid var(--bf-border)" }}
              data-testid={`publish-check-${it.id}`}
            >
              {it.done ? (
                <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: "var(--bf-primary)" }} />
              ) : (
                <Circle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: "var(--bf-text-muted)" }} />
              )}
              <div className="min-w-0">
                <p className="text-sm font-medium">{it.label}</p>
                {!it.done && (
                  <p className="text-xs mt-0.5" style={{ color: "var(--bf-text-muted)" }}>{it.hint}</p>
                )}
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* PROVIDER CARDS */}
      <section data-testid="publish-providers">
        <h3 className="font-serif text-2xl mb-4">Send to platform</h3>
        {enabledProviders.length === 0 && (
          <p className="text-sm" style={{ color: "var(--bf-text-muted)" }} data-testid="publish-no-providers">
            No publishing platforms are enabled by the admin yet.
          </p>
        )}
        <div className="grid sm:grid-cols-2 gap-4">
          {enabledProviders.map((p) => (
            <div
              key={p.id}
              className="p-5 rounded-lg flex flex-col gap-3"
              style={{ background: "white", border: "1px solid var(--bf-border)" }}
              data-testid={`publish-provider-${p.id}`}
            >
              <div className="flex items-start gap-2">
                {p.id === "self_publish" ? <Package className="w-5 h-5 mt-0.5" style={{ color: "var(--bf-primary)" }} />
                  : <BookOpen className="w-5 h-5 mt-0.5" style={{ color: "var(--bf-accent)" }} />}
                <div className="flex-1">
                  <p className="font-serif text-lg leading-tight">{p.label}</p>
                  <p className="text-xs mt-1" style={{ color: "var(--bf-text-muted)" }}>{p.tagline}</p>
                </div>
              </div>
              {p.id === "self_publish" ? (
                <Button
                  onClick={downloadBundle}
                  disabled={bundling}
                  className="text-white mt-auto"
                  style={{ background: "var(--bf-primary)" }}
                  data-testid="publish-bundle-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {bundling ? "Building…" : "Download bundle (.zip)"}
                </Button>
              ) : (
                <div className="flex flex-col sm:flex-row gap-2 mt-auto">
                  <a
                    href={review_done ? p.deep_link : "#"}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => { if (!review_done) e.preventDefault(); }}
                    className={`flex-1 ${review_done ? "" : "pointer-events-none opacity-50"}`}
                    data-testid={`publish-open-${p.id}`}
                  >
                    <Button variant="outline" className="w-full" disabled={!review_done}>
                      <ExternalLink className="w-4 h-4 mr-2" /> {review_done ? "Open dashboard" : "Run review first"}
                    </Button>
                  </a>
                  <Button variant="secondary" onClick={copyMetadata} disabled={!review_done} data-testid={`publish-copy-meta-${p.id}`}>
                    Copy metadata
                  </Button>
                </div>
              )}
              {p.needs_isbn && !book?.isbn && (
                <p className="text-xs" style={{ color: "var(--bf-primary)" }}>Add an ISBN in Settings before submitting here.</p>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* TYPESET PDF (always available, gated by review for parity) */}
      <section data-testid="publish-typeset-section">
        <h3 className="font-serif text-2xl mb-3">Typesetting PDF</h3>
        <p className="text-sm mb-4" style={{ color: "var(--bf-text-muted)" }}>
          Book-grade PDF with chapter title pages, drop caps, running headers, page numbers, table of contents, and a copyright page. Recommended trim size: <strong>6×9″</strong> (standard for paperback trade and KDP).
        </p>
        <div className="flex flex-wrap gap-2" data-testid="publish-typeset-buttons">
          <Button
            onClick={() => downloadTypeset("6x9")}
            className="text-white"
            style={{ background: "var(--bf-primary)" }}
            data-testid="publish-typeset-6x9-btn"
          >
            <Download className="w-4 h-4 mr-2" /> 6×9″ (recommended)
          </Button>
          <Button
            variant="outline"
            onClick={() => downloadTypeset("5.5x8.5")}
            data-testid="publish-typeset-5.5x8.5-btn"
          >
            <Download className="w-4 h-4 mr-2" /> 5.5×8.5″ (digest)
          </Button>
        </div>
      </section>

      {/* AI KDP KEYWORDS */}
      <section data-testid="publish-kdp-keywords">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <h3 className="font-serif text-2xl">KDP keywords & categories</h3>
          <Button onClick={runKdpKeywords} disabled={kdpBusy || !review_done} className="text-white" style={{ background: "var(--bf-primary)" }} data-testid="publish-kdp-run-btn">
            <Sparkles className="w-4 h-4 mr-2" /> {kdpBusy ? "Generating…" : review_done ? "Generate with AI" : "Run review first"}
          </Button>
        </div>
        <p className="text-xs mb-4" style={{ color: "var(--bf-text-muted)" }}>
          KDP allows exactly 7 keywords + 2 categories. Paste these directly into the KDP listing form.
        </p>
        {kdpResult && (
          <pre
            className="p-4 rounded text-xs whitespace-pre-wrap"
            style={{ background: "var(--bf-surface)", border: "1px solid var(--bf-border)" }}
            data-testid="publish-kdp-result"
          >
            {kdpResult}
          </pre>
        )}
      </section>
    </div>
  );
}
